import React from 'react';
import { Tooltip } from '@mantine/core';
import { ClipLoader } from 'react-spinners';
import { TbCopy } from 'react-icons/tb';
import type { WorkflowStage } from '../../../hooks/useWorkflowStages';
import { ActionIconButton, StepButton, StepHead, SubLabel, SubLabelRow } from './style';

interface StepItemProps {
  stage: WorkflowStage;
  index: number;
  onClick?: (stage: WorkflowStage) => void;
  /** True when the page being viewed represents this stage. */
  isActive?: boolean;
  /** Fires when the user clicks the inline shortcut icon (not the label). */
  onActionClick?: (stage: WorkflowStage) => void;
  /** True while the shortcut mutation for this stage is in flight. */
  isActionLoading?: boolean;
}

// Em-dash placeholder for stages that don't yet have a meaningful sub-label
// (typically `upcoming` stages like Submission before anything has happened).
const EMPTY_SUB_LABEL = '—';

const StepItem: React.FC<StepItemProps> = ({
  stage,
  onClick,
  isActive,
  onActionClick,
  isActionLoading
}) => {
  // A stage is clickable as long as it resolves to a real path. We
  // intentionally don't block `upcoming` stages anymore — e.g. AIFs and
  // Source Files share the same Funds page, so blocking Source Files when
  // AIFs is current produces the "link doesn't work" report.
  const hasPath = !!stage.path && !stage.path.startsWith('#');
  const subLabel = stage.subLabel || EMPTY_SUB_LABEL;
  // Only surface the shortcut on the workflow's current step — done stages
  // don't need re-copying, and upcoming stages can't be short-circuited past
  // the current one. Independent of `isActive` (the URL-driven page focus).
  const hasAction = !!stage.action && !!onActionClick && stage.status === 'current';

  return (
    <StepHead $active={!!isActive}>
      <StepButton
        type="button"
        onClick={hasPath && onClick ? () => onClick(stage) : undefined}
        disabled={!hasPath}
        $disabled={!hasPath}
        $active={!!isActive}
        aria-current={isActive ? 'page' : stage.status === 'current' ? 'step' : undefined}
      >
        <span className="label">{stage.label}</span>
        <SubLabelRow>
          <SubLabel className="sub-label" $tone={stage.tone}>
            {subLabel}
          </SubLabel>
          {stage.isLoading && <ClipLoader size={10} color="#228BE6" />}
          {hasAction && (
            <Tooltip label={stage.action!.label} withArrow position="bottom">
              <ActionIconButton
                type="button"
                aria-label={stage.action!.label}
                disabled={!!isActionLoading}
                onClick={e => {
                  // Stop the click from bubbling to the parent StepButton —
                  // otherwise the click would navigate to the stage's page
                  // in addition to running the shortcut.
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isActionLoading) onActionClick?.(stage);
                }}
              >
                {isActionLoading ? <ClipLoader size={12} color="#228BE6" /> : <TbCopy size={14} />}
              </ActionIconButton>
            </Tooltip>
          )}
        </SubLabelRow>
      </StepButton>
    </StepHead>
  );
};

export default StepItem;
